import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useContext, useRef } from "react";
import { ProductContext } from "../contexts/ProductContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Breadcrumb from "../components/Breadcrumb";
import BASE_URL from "../BASEURL";

export default function ProductDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { products: ctxProducts, setProducts: setCtxProducts } =
    useContext(ProductContext);

  const productFromState =
    location.state && location.state.product
      ? location.state.product
      : null;

  const productFromCtx = ctxProducts
    ? ctxProducts.find((p) => (p._id || p.id) === id)
    : null;

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [relatedProducts, setRelatedProducts] = useState([]);

  // Scroll to top on ID change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  // Fetch product + related
  useEffect(() => {
    const fetchProductAndRelated = async () => {
      setLoading(true);
      setError("");

      let current = productFromState || productFromCtx;

      try {
        // Use cached product first (fast UI)
        if (current) {
          setProduct(current);
        }

        // Always fetch latest data
        const res = await fetch(`${BASE_URL}/api/product/${id}`);
        if (!res.ok) throw new Error("Failed to fetch product");

        const json = await res.json();
        current = json.data;
        setProduct(current);

        // Add to context cache
        if (
          ctxProducts &&
          !ctxProducts.find(
            (p) => (p._id || p.id) === (current._id || current.id)
          )
        ) {
          setCtxProducts([...ctxProducts, current]);
        }

        // Fetch related products
        const categoryId =
          current.category && typeof current.category === "object"
            ? current.category._id
            : current.category;

        if (categoryId) {
          const resRelated = await fetch(
            `${BASE_URL}/api/product/category/${categoryId}`
          );
          if (!resRelated.ok)
            throw new Error("Failed to fetch related products");

          const jsonRelated = await resRelated.json();

          const related = (jsonRelated.data || [])
            .filter((item) => item._id !== id)
            .slice(0, 3);

          setRelatedProducts(related);
        } else {
          setRelatedProducts([]);
        }
      } catch (err) {
        setError(err.message || "Failed to fetch product");
      } finally {
        setLoading(false);

        // Scroll to product section
        setTimeout(() => {
          const element = document.getElementById("product-detail");
          if (element) {
            const top =
              element.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({ top: top - 130, behavior: "smooth" });
          }
        }, 200);
      }
    };

    fetchProductAndRelated();
  }, [id]); // ✅ only id dependency

  // YouTube embed helper
  const getYouTubeEmbedUrl = (url) => {
    if (!url || typeof url !== "string") return null;

    try {
      const u = new URL(url);
      const host = u.hostname.toLowerCase();
      let vid = null;

      if (host.includes("youtu.be")) {
        vid = u.pathname.slice(1);
      } else if (host.includes("youtube.com")) {
        vid = u.searchParams.get("v");

        if (!vid) {
          const parts = u.pathname.split("/").filter(Boolean);
          if (parts.length) vid = parts[parts.length - 1];
        }
      }

      if (!vid) return null;

      return `https://www.youtube.com/embed/${vid}`;
    } catch {
      return null;
    }
  };

  return (
    <>
      <Navbar />

      <div className="bg-dark">
        <Breadcrumb
          productName={product?.name}
          category={product?.category}
        />
      </div>

      <div
        className="max-w-7xl mx-auto p-10 md:block"
        id="product-detail"
      >
        <h1 className="text-3xl font-bold text-primary mb-6">
          Product Detail
        </h1>

        {loading && <p>Loading...</p>}
        {error && <p className="text-red-500">{error}</p>}

        {!loading && !error && product && (
          <>
            {/* PRODUCT CARD */}
            <div className="mt-10 p-6 bg-white rounded-xl shadow">
              <div className="flex flex-col md:flex-row gap-8">
                {/* IMAGE */}
                <div className="md:w-1/3 flex items-center justify-center">
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="max-h-60 w-full object-contain"
                    />
                  ) : (
                    <div className="h-60 w-full flex items-center justify-center bg-gray-100 text-gray-400">
                      No Image Available
                    </div>
                  )}
                </div>

                {/* DETAILS */}
                <div className="md:w-2/3">
                  <h2 className="text-2xl font-bold text-primary mb-4">
                    {product.name}
                  </h2>

                  <p className="text-gray-700 mb-4">
                    {Array.isArray(product.shortDesc)
                      ? product.shortDesc
                          .map((d) =>
                            typeof d === "string"
                              ? d
                              : d?.name || JSON.stringify(d)
                          )
                          .join(", ")
                      : product.shortDesc?.name ||
                        product.shortDesc}
                  </p>

                  <p className="text-gray-600 mb-4">
                    {product.description}
                  </p>

                  <p className="text-sm text-gray-500 mb-4">
                    Category:{" "}
                    {product.category?.name || product.category}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {product.features?.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-2">
                          Features
                        </h3>
                        <ul className="list-disc pl-5">
                          {product.features.map((f, i) => (
                            <li key={i}>{f}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {product.applications?.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-2">
                          Applications
                        </h3>
                        <ul className="list-disc pl-5">
                          {product.applications.map((a, i) => (
                            <li key={i}>{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* VIDEO + BROCHURE */}
            <div className="mt-12 grid md:grid-cols-2 gap-8">
              <div>
                <h2 className="text-xl font-bold mb-4">
                  Product Video
                </h2>

                {product.videoUrl ? (
                  getYouTubeEmbedUrl(product.videoUrl) ? (
                    <iframe
                      src={getYouTubeEmbedUrl(product.videoUrl)}
                      className="w-full h-64 rounded-xl"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      controls
                      className="w-full h-64 rounded-xl"
                    >
                      <source src={product.videoUrl} />
                    </video>
                  )
                ) : (
                  <div className="h-64 flex items-center justify-center bg-gray-100">
                    Coming Soon
                  </div>
                )}
              </div>

              <div>
                <h2 className="text-xl font-bold mb-4">
                  Download Brochure
                </h2>

                {product.brochureUrl ? (
                  <a
                    href={product.brochureUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="bg-primary text-white px-6 py-2 rounded"
                  >
                    Download PDF
                  </a>
                ) : (
                  <button
                    disabled
                    className="bg-gray-400 text-white px-6 py-2 rounded"
                  >
                    Not Available
                  </button>
                )}
              </div>
            </div>

            {/* RELATED */}
            <div className="mt-16">
              <h2 className="text-xl font-bold mb-6">
                Related Products
              </h2>

              <div className="grid md:grid-cols-3 gap-6">
                {relatedProducts.map((item) => (
                  <div
                    key={item._id}
                    onClick={() =>
                      navigate(`/product/${item._id}`, {
                        state: { product: item },
                      })
                    }
                    className="bg-white p-4 rounded-xl shadow cursor-pointer hover:scale-105 transition"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-24 mx-auto mb-3"
                    />
                    <h3 className="text-center font-semibold">
                      {item.name}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <Footer />
    </>
  );
}